<template>
  <div class="subscribe-view">
    <div v-if="loading" class="loading">
      Loading plan details...
    </div>
    <div v-else-if="error" class="error">
      {{ error }}
    </div>
    <div v-else-if="plan" class="subscribe-content">
      <div class="back-button-container">
        <button @click="goBack" class="back-button">
          ← Back to Plans
        </button>
      </div>

      <div class="plan-summary">
        <h2>{{ plan.name }}</h2>
        <div class="summary-price">
          <span class="price-amount">${{ plan.finalPrice.toFixed(2) }}</span>
          <span v-if="plan.basePrice !== plan.finalPrice" class="original-price">
            ${{ plan.basePrice.toFixed(2) }}
          </span>
          <span class="price-period">/ {{ plan.billingCycle }}</span>
        </div>
        <div v-if="plan.discount && plan.discount.isActive" class="discount-badge">
          {{ formatDiscount(plan.discount) }}
        </div>
        <div class="summary-features">
          <div v-if="plan.siteCount !== null" class="summary-feature">
            <span class="feature-icon">✓</span>
            <span>{{ plan.siteCount }} Site{{ plan.siteCount !== 1 ? 's' : '' }}</span>
          </div>
          <div v-if="plan.inodes !== null" class="summary-feature">
            <span class="feature-icon">✓</span>
            <span>{{ formatInodes(plan.inodes) }} Inodes</span>
          </div>
          <div v-if="plan.description" class="summary-feature">
            <span class="feature-icon">✓</span>
            <span>{{ plan.description }}</span>
          </div>
        </div>
      </div>

      <div class="payment-section">
        <h3>Payment Information</h3>
        <form @submit.prevent="handleSubmit" class="payment-form" id="payment-form">
          <div class="form-group">
            <label for="cardNumber">Card Number</label>
            <input
              type="text"
              id="cardNumber"
              v-model="paymentForm.cardNumber"
              placeholder="1234 5678 9012 3456"
              maxlength="19"
              @input="formatCardNumber"
              required
              class="form-input"
            />
          </div>

          <div class="card-row">
            <div class="form-group">
              <label for="expiry">Expiry Date</label>
              <input
                type="text"
                id="expiry"
                v-model="paymentForm.expiry"
                placeholder="MM/YY"
                maxlength="5"
                @input="formatExpiry"
                required
                class="form-input"
              />
            </div>

            <div class="form-group">
              <label for="cvc">CVC</label>
              <input
                type="text"
                id="cvc"
                v-model="paymentForm.cvc"
                placeholder="123"
                maxlength="4"
                @input="formatCVC"
                required
                class="form-input"
              />
            </div>
          </div>

          <div class="form-group">
            <label for="name">Cardholder Name</label>
            <input
              type="text"
              id="name"
              v-model="paymentForm.name"
              placeholder="John Doe"
              required
              class="form-input"
            />
          </div>

          <div class="form-group">
            <label for="email">Email</label>
            <input
              type="email"
              id="email"
              v-model="paymentForm.email"
              placeholder="john@example.com"
              required
              class="form-input"
            />
          </div>

          <div v-if="cardError" class="card-errors" role="alert">
            {{ cardError }}
          </div>

          <div class="form-actions">
            <button type="submit" class="submit-button" :disabled="processing">
              {{ processing ? 'Processing...' : `Subscribe for $${plan.finalPrice.toFixed(2)}/${plan.billingCycle}` }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { api } from '../utils/api'

export default {
  name: 'SubscribeView',
  data() {
    return {
      plan: null,
      loading: true,
      error: null,
      processing: false,
      cardError: null,
      paymentForm: {
        cardNumber: '',
        expiry: '',
        cvc: '',
        name: '',
        email: ''
      }
    }
  },
  async created() {
    await this.initializePlan()
  },
  methods: {
    async initializePlan() {
      try {
        this.loading = true
        this.error = null

        // Try to get plan from query params first (passed from plans view)
        const planParam = this.$route.query.plan
        if (planParam) {
          try {
            this.plan = JSON.parse(planParam)
            this.loading = false
            return
          } catch (e) {
            console.warn('Failed to parse plan from query params:', e)
          }
        }

        // Otherwise fetch from API using planId
        const planId = this.$route.params.planId
        if (planId) {
          const plans = await api('/subscription-plans', {}, this.$router)
          const planArray = Array.isArray(plans) ? plans : []
          this.plan = planArray.find(p => p.id === planId)
          
          if (!this.plan) {
            this.error = 'Plan not found'
          }
        } else {
          this.error = 'No plan specified'
        }
      } catch (error) {
        this.error = error.code || 'Failed to load plan details. Please try again.'
        console.error('Error loading plan:', error)
      } finally {
        this.loading = false
      }
    },
    formatCardNumber(event) {
      let value = event.target.value.replace(/\s+/g, '').replace(/[^0-9]/gi, '')
      let formattedValue = value.match(/.{1,4}/g)?.join(' ') || value
      if (formattedValue.length > 19) {
        formattedValue = formattedValue.substr(0, 19)
      }
      this.paymentForm.cardNumber = formattedValue
      this.validateCard()
    },
    formatExpiry(event) {
      let value = event.target.value.replace(/\D/g, '')
      if (value.length >= 2) {
        value = value.substring(0, 2) + '/' + value.substring(2, 4)
      }
      this.paymentForm.expiry = value
      this.validateCard()
    },
    formatCVC(event) {
      let value = event.target.value.replace(/\D/g, '')
      this.paymentForm.cvc = value
      this.validateCard()
    },
    validateCard() {
      this.cardError = null
      
      // Basic validation
      const cardNumber = this.paymentForm.cardNumber.replace(/\s/g, '')
      if (cardNumber && cardNumber.length < 13) {
        this.cardError = 'Card number must be at least 13 digits'
        return false
      }
      
      const expiry = this.paymentForm.expiry
      if (expiry && expiry.length === 5) {
        const [month] = expiry.split('/')
        const monthNum = parseInt(month, 10)
        if (monthNum < 1 || monthNum > 12) {
          this.cardError = 'Invalid expiry month'
          return false
        }
      }
      
      if (this.paymentForm.cvc && this.paymentForm.cvc.length < 3) {
        this.cardError = 'CVC must be at least 3 digits'
        return false
      }
      
      return true
    },
    async handleSubmit() {
      if (!this.plan) {
        this.error = 'Plan not loaded. Please refresh the page.'
        return
      }

      // Validate card fields
      if (!this.validateCard()) {
        return
      }

      this.processing = true
      this.error = null
      this.cardError = null

      try {
        // Prepare request body
        const requestBody = {
          planId: this.plan.id
        }

        // Add discountId if discount exists and is active
        if (this.plan.discount && this.plan.discount.isActive && this.plan.discount.id) {
          requestBody.discountId = this.plan.discount.id
        }

        // Submit subscription upgrade to backend
        await api('/subscriptions/change', {
          method: 'POST',
          body: JSON.stringify(requestBody),
        }, this.$router)

        // Success - redirect to success page or profile
        this.$router.push({
          name: 'Profile',
          query: { subscribed: 'true' }
        })
      } catch (error) {
        console.error('Subscription error:', error)
        this.error = error.code || error.message || 'Failed to process subscription. Please try again.'
      } finally {
        this.processing = false
      }
    },
    formatDiscount(discount) {
      if (!discount || !discount.isActive) return ''
      
      if (discount.discountType === 'percentage') {
        return `${discount.discountValue}% OFF`
      } else {
        return `$${discount.discountValue} OFF`
      }
    },
    formatInodes(inodes) {
      if (inodes === null || inodes === undefined) return 'Unlimited'
      return inodes.toLocaleString('en-US')
    },
    goBack() {
      this.$router.push({ name: 'UserSubscriptionPlans' })
    }
  }
}
</script>

<style scoped>
.subscribe-view {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.loading, .error {
  text-align: center;
  padding: 40px 20px;
  font-size: 1.2em;
  color: var(--text-primary);
}

.error {
  color: var(--error-color);
  background-color: var(--error-bg);
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.back-button-container {
  margin-bottom: 24px;
}

.back-button {
  background: none;
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
}

.back-button:hover {
  background-color: var(--bg-tertiary);
  border-color: var(--accent-color);
}

.subscribe-content {
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 32px;
}

.plan-summary {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 24px;
  height: fit-content;
  position: sticky;
  top: 20px;
}

.plan-summary h2 {
  color: var(--text-primary);
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 16px 0;
}

.summary-price {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.price-amount {
  color: var(--text-primary);
  font-size: 32px;
  font-weight: 700;
}

.original-price {
  color: var(--text-muted);
  font-size: 18px;
  text-decoration: line-through;
}

.price-period {
  color: var(--text-secondary);
  font-size: 16px;
  font-weight: 500;
}

.discount-badge {
  display: inline-block;
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%);
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 20px;
}

.summary-features {
  margin-top: 24px;
}

.summary-feature {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color);
}

.summary-feature:last-child {
  border-bottom: none;
}

.feature-icon {
  color: var(--accent-color);
  font-weight: 700;
  font-size: 18px;
}

.payment-section {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 24px;
}

.payment-section h3 {
  color: var(--text-primary);
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 24px 0;
}

.payment-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.card-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.card-errors {
  color: var(--error-color);
  font-size: 14px;
  margin-top: -8px;
  display: block;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  color: var(--text-primary);
  font-weight: 500;
  font-size: 14px;
}

.form-input {
  padding: 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--bg-tertiary);
  color: var(--text-primary);
  font-size: 16px;
  transition: border-color 0.2s ease;
}

.form-input:focus {
  outline: none;
  border-color: var(--accent-color);
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.form-input::placeholder {
  color: var(--text-muted);
}

.form-actions {
  margin-top: 8px;
}

.submit-button {
  width: 100%;
  padding: 14px 24px;
  background-color: var(--accent-color);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.submit-button:hover:not(:disabled) {
  background-color: var(--accent-hover, #0056b3);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.submit-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .subscribe-content {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .plan-summary {
    position: static;
  }
}
</style>

